import { get, remove, set } from 'lib/storage/expirableLocalStorage'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

/**
 * Store a rejected loyalty package upgrade in local storage so it is temporarily hidden on the
 * offer page. Hide only the upgrade target; keep the source package bookable.
 *
 * The one-hour expiry matches `config.blockRate.defaultBlockTimeMs` in svc-bedbank.
 */
const REJECTED_UPGRADE_TTL_SECONDS = 60 * 60

export interface RejectedUpgradeIdentity {
  propertyId: string
  /**
   * Use `rate.service.rateId`, which remains stable across availability fetches. A property rate's
   * `rateId` is regenerated on each fetch and cannot identify the rejected pair later.
   */
  sourceRateId: string
  targetRateId: string
  checkIn: string
  checkOut: string
  occupancy: App.Occupants | undefined
}

/** Scoped to the exact stay — other dates and party sizes are priced separately. */
function buildKey({ propertyId, sourceRateId, targetRateId, checkIn, checkOut, occupancy }: RejectedUpgradeIdentity): string {
  const occupancyKey = occupancy ? generateOccupancyStringByRoom(occupancy) : ''
  return `loyalty_rejected_upgrade:${propertyId}:${sourceRateId}:${targetRateId}:${checkIn}:${checkOut}:${occupancyKey}`
}

export function recordRejectedPropertyRateUpgrade(identity: RejectedUpgradeIdentity): void {
  set(buildKey(identity), true, REJECTED_UPGRADE_TTL_SECONDS)
}

/**
 * Remove the temporary rejection when a re-check confirms the loyalty package upgrade still exists.
 */
export function clearRejectedPropertyRateUpgrade(identity: RejectedUpgradeIdentity): void {
  remove(buildKey(identity))
}

export function isPropertyRateUpgradeRejected(identity: RejectedUpgradeIdentity): boolean {
  return get<boolean>(buildKey(identity)) === true
}
